import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { ReservationGuestEditor } from "@/components/ReservationGuestEditor";
import { hasGuestBreakdown, GuestSummary } from "@/components/GuestSummary";
import { validGuestCounts } from "@/components/GuestCountFields";
import { reservationErrorMessage } from "@/lib/reservationErrors";
import { toast } from "sonner";
import { format, parse, addDays } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar as CalendarIcon, CheckCircle, AlertCircle, Home } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageReservation = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  
  const [editDate, setEditDate] = useState(new Date());
  const [editTime, setEditTime] = useState("");
  const [editGuestCounts, setEditGuestCounts] = useState(null);
  const editGuestCount = editGuestCounts ? editGuestCounts.adults_count + editGuestCounts.children_count : reservation?.guests_count;
  const [editSpecialRequests, setEditSpecialRequests] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");

  useEffect(() => {
    fetchReservation();
    fetchBlockedSlots();
  }, [reservationId]);

  const fetchReservation = async () => {
    try {
      const response = await axios.get(`${API}/reservations/${reservationId}`);
      setReservation(response.data);
      setEditDate(parse(response.data.reservation_date, "yyyy-MM-dd", new Date()));
      setEditTime(response.data.reservation_time);
      setEditGuestCounts(hasGuestBreakdown(response.data) ? {
        adults_count: response.data.adults_count, children_count: response.data.children_count
      } : null);
      setEditSpecialRequests(response.data.special_requests || "");
      setEditFirstName(response.data.first_name);
      setEditLastName(response.data.last_name);
      setEditEmail(response.data.email);
      setEditPhone(response.data.phone);
    } catch (error) {
      toast.error("Reservierung nicht gefunden");
      setTimeout(() => navigate("/"), 2000);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedSlots = async () => {
    try {
      const response = await axios.get(`${API}/blocked-slots`);
      setBlockedSlots(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der gesperrten Slots:", error);
    }
  };

  const generateTimeSlots = () => {
    const slots = [];
    const dayName = format(editDate, 'EEEE').toLowerCase();
    
    // Weekday lunch slots starting at 11:00
    if (['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(dayName)) {
      let hour = 11;
      let minute = 0;
      while (hour < 14 || (hour === 14 && minute === 0)) {
        slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
        minute += 15;
        if (minute === 60) {
          minute = 0;
          hour += 1;
        }
      }
    }
    
    // Dinner slots (all days)
    let hour = 17;
    let minute = 0;
    while (hour < 21 || (hour === 21 && minute <= 45)) {
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
      minute += 15;
      if (minute === 60) {
        minute = 0;
        hour += 1;
      }
    }
    
    return slots;
  };

  const isSlotBlocked = (time) => {
    const dateStr = format(editDate, "yyyy-MM-dd");
    return blockedSlots.some(slot => {
      if (slot.date !== dateStr) return false;
      if (slot.is_full_day) return true;
      if (slot.time_from && slot.time_to) {
        return time >= slot.time_from && time <= slot.time_to;
      }
      return false;
    });
  };

  const handleUpdate = async () => {
    if (saving) return;
    if (editGuestCounts && !validGuestCounts(editGuestCounts)) {
      toast.error("Bitte wählen Sie mindestens eine erwachsene Person.");
      return;
    }
    setSaving(true);
    try {
      const updateData = {
        first_name: editFirstName,
        last_name: editLastName,
        email: editEmail,
        phone: editPhone,
        reservation_date: format(editDate, "yyyy-MM-dd"),
        reservation_time: editTime,
        ...(editGuestCounts || {}),
        special_requests: editSpecialRequests
      };

      await axios.put(`${API}/reservations/${reservationId}/customer`, updateData);
      toast.success("Änderungen gespeichert! Eine Bestätigung wurde per E-Mail gesendet.");
      setTimeout(() => fetchReservation(), 1000);
    } catch (error) {
      toast.error(reservationErrorMessage(error, "Fehler beim Speichern"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Möchten Sie diese Reservierung wirklich stornieren?")) {
      return;
    }

    setSaving(true);
    try {
      await axios.delete(`${API}/reservations/${reservationId}/customer`);
      toast.success("Reservierung storniert! Eine Bestätigung wurde per E-Mail gesendet.");
      setTimeout(() => fetchReservation(), 1000);
    } catch (error) {
      toast.error("Fehler beim Stornieren");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#f8f6f3'}}>
        <div className="text-xl" style={{color: '#2c2c2c'}}>Lädt...</div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#f8f6f3'}}>
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4" style={{color: '#2c2c2c'}}>Reservierung nicht gefunden</h1>
          <Button onClick={() => navigate("/")} style={{backgroundColor: '#c8232c'}} className="text-white">
            Zur Startseite
          </Button>
        </div>
      </div>
    );
  }

  const getStatusIcon = () => {
    switch (reservation.status) {
      case 'confirmed':
        return <CheckCircle className="w-12 h-12 text-green-600" />;
      case 'pending':
        return <AlertCircle className="w-12 h-12 text-orange-600" />;
      case 'cancelled':
        return <div className="w-12 h-12 text-red-600 text-5xl">✕</div>;
      default:
        return <CheckCircle className="w-12 h-12 text-blue-600" />;
    }
  };

  const getStatusText = () => {
    switch (reservation.status) {
      case 'confirmed':
        return 'Bestätigt';
      case 'pending':
        return 'Anfrage (Warten auf Bestätigung)';
      case 'cancelled':
        return 'Storniert';
      case 'completed':
        return 'Abgeschlossen';
      default:
        return reservation.status;
    }
  };

  const getStatusColor = () => {
    switch (reservation.status) {
      case 'confirmed':
        return '#e8f5e9';
      case 'pending':
        return '#fff3e0';
      case 'cancelled':
        return '#ffebee';
      case 'completed':
        return '#e3f2fd';
      default:
        return '#f5f5f5';
    }
  };

  const timeSlots = generateTimeSlots();

  return (
    <div className="min-h-screen py-6 md:py-12 px-3 md:px-4" style={{backgroundColor: '#f8f6f3'}}>
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-4 md:mb-6 text-sm md:text-base"
          style={{color: '#2c2c2c'}}
        >
          <Home className="mr-2 h-3 w-3 md:h-4 md:w-4" />
          Zur Startseite
        </Button>

        <div className="bg-white rounded-xl md:rounded-2xl shadow-xl p-4 md:p-8">
          <div className="text-center mb-6 md:mb-8">
            <div className="flex justify-center mb-3 md:mb-4">
              <img 
                src={process.env.REACT_APP_LOGO_URL} 
                alt="Pizzeria Piazza Logo" 
                className="h-14 md:h-20 w-auto"
              />
            </div>
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4" style={{backgroundColor: getStatusColor()}}>
              {getStatusIcon()}
            </div>
            <h1 className="text-xl md:text-3xl font-bold mb-2" style={{color: '#2c2c2c'}}>
              Reservierung Verwalten
            </h1>
            <div className="italian-flag-accent mb-3 md:mb-4"></div>
            <p className="text-base md:text-lg font-semibold mb-2" style={{color: reservation.status === 'confirmed' ? '#4caf50' : reservation.status === 'pending' ? '#ff9800' : '#f44336'}}>
              Status: {getStatusText()}
            </p>
            <p className="text-xs md:text-sm text-gray-600">
              {reservation.first_name} {reservation.last_name}
            </p>
          </div>

          <div className="mb-6 text-sm" data-testid="manage-current-guests">
            <GuestSummary reservation={reservation} prefix="manage-saved" />
          </div>
          {reservation.status === 'cancelled' ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-6">Diese Reservierung wurde storniert.</p>
              <Button
                onClick={() => navigate("/")}
                className="text-white"
                style={{backgroundColor: '#c8232c'}}
              >
                Neue Reservierung
              </Button>
            </div>
          ) : reservation.status === 'completed' ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-2">Diese Reservierung wurde abgeschlossen.</p>
              <p className="text-gray-600 mb-6">Wir hoffen, Sie hatten einen angenehmen Aufenthalt!</p>
              <Button
                onClick={() => navigate("/")}
                className="text-white"
                style={{backgroundColor: '#c8232c'}}
              >
                Neue Reservierung
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <Label className="text-xs md:text-sm mb-2 block font-semibold">Vorname</Label>
                  <Input
                    data-testid="manage-first-name"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    className="border-2 h-10 md:h-10 text-sm md:text-base"
                  />
                </div>

                <div>
                  <Label className="text-xs md:text-sm mb-2 block font-semibold">Nachname</Label>
                  <Input
                    data-testid="manage-last-name"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    className="border-2 h-10 md:h-10 text-sm md:text-base"
                  />
                </div>

                <div>
                  <Label className="text-xs md:text-sm mb-2 block font-semibold">E-Mail</Label>
                  <Input
                    data-testid="manage-email"
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="border-2 h-10 md:h-10 text-sm md:text-base"
                  />
                </div>

                <div>
                  <Label className="text-xs md:text-sm mb-2 block font-semibold">Telefon</Label>
                  <Input
                    data-testid="manage-phone"
                    type="tel"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    className="border-2 h-10 md:h-10 text-sm md:text-base"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div className="relative">
                  <Label className="text-xs md:text-sm mb-2 block font-semibold">Datum</Label>
                  <Button
                    data-testid="manage-date-button"
                    type="button"
                    variant="outline"
                    onClick={() => setShowCalendar(!showCalendar)}
                    className="w-full justify-start text-left border-2 h-10 text-xs md:text-sm"
                  >
                    <CalendarIcon className="mr-2 h-3 w-3 md:h-4 md:w-4" />
                    {format(editDate, "dd.MM.yyyy", { locale: de })}
                  </Button>
                  {showCalendar && (
                    <div className="absolute z-50 mt-2 border rounded-md p-2 md:p-3 bg-white shadow-lg max-w-[300px]">
                      <Calendar
                        mode="single"
                        selected={editDate}
                        onSelect={(date) => {
                          setEditDate(date);
                          setShowCalendar(false);
                        }}
                        disabled={(date) => date < new Date() || date > addDays(new Date(), 90)}
                        locale={de}
                        initialFocus
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-sm mb-2 block font-semibold">Uhrzeit</Label>
                  <select
                    data-testid="manage-time-select"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                    className="w-full p-2 border-2 rounded-md"
                  >
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot} disabled={isSlotBlocked(slot)}>
                        {slot} Uhr {isSlotBlocked(slot) ? '(Gesperrt)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <ReservationGuestEditor reservation={reservation} value={editGuestCounts}
                    onChange={setEditGuestCounts} prefix="manage" disabled={saving} />
                  {editGuestCount >= 15 && (
                    <p data-testid="manage-large-group-notice" className="text-xs text-orange-600 mt-1">Ab 15 Personen: Bestätigung durch das Restaurant erforderlich</p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-sm mb-2 block font-semibold">Spezielle Wünsche</Label>
                <Textarea
                  data-testid="manage-special-requests"
                  value={editSpecialRequests}
                  onChange={(e) => setEditSpecialRequests(e.target.value)}
                  className="border-2"
                  rows={3}
                  placeholder="Allergien, Unverträglichkeiten, besondere Anlässe..."
                />
              </div>

              <div className="border-t pt-6">
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600"><strong>Reservierungs-ID:</strong> {reservation.id}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    data-testid="manage-save-button"
                    onClick={handleUpdate}
                    disabled={saving || (editGuestCounts !== null && !validGuestCounts(editGuestCounts))}
                    className="flex-1 text-white py-6 text-lg"
                    style={{backgroundColor: '#4caf50'}}
                  >
                    {saving ? "Speichert..." : "Änderungen Speichern"}
                  </Button>
                  <Button
                    data-testid="manage-cancel-button"
                    onClick={handleCancel}
                    disabled={saving}
                    variant="destructive"
                    className="flex-1 py-6 text-lg"
                  >
                    Reservierung Stornieren
                  </Button>
                </div>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                Nach dem Speichern erhalten Sie eine Bestätigung per E-Mail
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageReservation;